import { useState, useEffect, useRef } from 'react'
import { User, Mail, Lock, Bell, Moon, Sun, Save, Building2, Camera, Trash2, CheckCircle, AlertCircle } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../lib/supabase'
import { getUserOrganizations, updateOrganization } from '../../services/organizationService'

export default function Settings() {
  const { user, setUserProfile: storeSetUserProfile } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [selectedOrg, setSelectedOrg] = useState(null)
  
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    avatar_url: ''
  })
  
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [avatarImgError, setAvatarImgError] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState('')
  const [avatarSuccess, setAvatarSuccess] = useState('')
  const avatarInputRef = useRef(null)
  
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: ''
  })
  
  const [orgData, setOrgData] = useState({
    name: '',
    description: ''
  })
  
  const [notifications, setNotifications] = useState({
    email_on_comment: true,
    email_on_approval: true,
    email_on_project_update: true
  })

  useEffect(() => {
    loadData()
    
    // Check dark mode
    const isDark = document.documentElement.classList.contains('dark')
    setDarkMode(isDark)
  }, [user])
  
  useEffect(() => {
    if (selectedOrg) {
      const org = organizations.find(o => o.id === selectedOrg)
      if (org) {
        setOrgData({
          name: org.name || '',
          description: org.description || ''
        })
      }
    }
  }, [selectedOrg, organizations])

  const loadData = async () => {
    if (!user) return
    
    try {
      // Load profile
      const { data: profile } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()
      
      if (profile) {
        console.log('[Settings] Loaded profile from DB:', { 
          full_name: profile.full_name,
          avatar_url: profile.avatar_url,
          hasAvatar: !!profile.avatar_url
        })
        setProfileData({
          full_name: profile.full_name || '',
          email: profile.email || user.email,
          avatar_url: profile.avatar_url || ''
        })
        setAvatarPreview(profile.avatar_url || null)
        setAvatarImgError(false)
      }
      
      // Load organizations
      const { data: orgs } = await getUserOrganizations(user.id)
      setOrganizations(orgs || [])
      
      if (orgs && orgs.length > 0) {
        setSelectedOrg(orgs[0].id)
      }
    } catch (error) {
      console.error('Error loading settings:', error)
    }
  }

  const showAvatarMessage = (type, msg) => {
    if (type === 'success') {
      setAvatarSuccess(msg)
      setAvatarError('')
      setTimeout(() => setAvatarSuccess(''), 4000)
    } else {
      setAvatarError(msg)
      setAvatarSuccess('')
      setTimeout(() => setAvatarError(''), 4000)
    }
  }

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    console.log('[Avatar] File selected:', { name: file.name, type: file.type, size: file.size })

    // Reset input so same file can be re-selected
    e.target.value = ''

    if (!file.type.startsWith('image/')) {
      showAvatarMessage('error', 'Please select a valid image file (JPG, PNG, GIF, WebP)')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      showAvatarMessage('error', 'Image must be smaller than 2MB')
      return
    }

    // Read file as base64 first (Promise-based to avoid race condition)
    const base64Preview = await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.readAsDataURL(file)
    })

    console.log('[Avatar] base64 preview ready, length:', base64Preview?.length)

    // Show base64 preview immediately — this is always displayable, no network needed
    setAvatarPreview(base64Preview)
    setUploadingAvatar(true)

    try {
      const fileExt = file.name.split('.').pop().toLowerCase()
      const fileName = `${user.id}-${Date.now()}.${fileExt}`
      const filePath = `avatars/${fileName}`

      console.log('[Avatar] Uploading to Storage path:', filePath)

      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('project-files')
        .upload(filePath, file, { cacheControl: '3600', upsert: true })

      console.log('[Avatar] Storage upload result:', { uploadData, uploadError })
      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('project-files')
        .getPublicUrl(filePath)

      console.log('[Avatar] Public URL:', publicUrl)

      const { error: updateError, data: updateData } = await supabase
        .from('users')
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('id', user.id)
        .select()

      console.log('[Avatar] DB update result:', { updateData, updateError })
      if (updateError) throw updateError

      // Keep base64 preview for Settings page (always works, no RLS dependency)
      // Save publicUrl to profileData and store for DB persistence & sidebar
      setProfileData(prev => ({ ...prev, avatar_url: publicUrl }))
      setAvatarImgError(false)
      storeSetUserProfile({ avatar_url: publicUrl })
      console.log('[Avatar] ✅ Upload complete. publicUrl saved, base64 stays as preview.')
      showAvatarMessage('success', 'Profile picture updated!')
    } catch (error) {
      console.error('[Avatar] ❌ Upload failed:', error)
      // Revert preview on failure
      setAvatarPreview(profileData.avatar_url || null)
      showAvatarMessage('error', error.message)
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleRemoveAvatar = async () => {
    if (!profileData.avatar_url) return
    setUploadingAvatar(true)
    try {
      const { error } = await supabase
        .from('users')
        .update({ avatar_url: null, updated_at: new Date().toISOString() })
        .eq('id', user.id)
      if (error) throw error
      setAvatarPreview(null)
      setProfileData(prev => ({ ...prev, avatar_url: '' }))
      // Sync to global store so sidebar updates immediately
      storeSetUserProfile({ avatar_url: null })
      showAvatarMessage('success', 'Profile picture removed.')
    } catch (error) {
      showAvatarMessage('error', error.message)
    } finally {
      setUploadingAvatar(false)
    }
  }
  
  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      const { error } = await supabase
        .from('users')
        .update({
          full_name: profileData.full_name,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
      
      if (error) throw error

      // Sync to global store so sidebar name updates immediately
      storeSetUserProfile({ full_name: profileData.full_name })
      alert('Profile updated successfully')
    } catch (error) {
      console.error('Error updating profile:', error)
      alert('Failed to update profile: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdatePassword = async (e) => {
    e.preventDefault()
    
    if (passwordData.new !== passwordData.confirm) {
      alert('New passwords do not match')
      return
    }
    
    setLoading(true)
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.new
      })
      
      if (error) throw error
      
      setPasswordData({ current: '', new: '', confirm: '' })
      alert('Password updated successfully')
    } catch (error) {
      console.error('Error updating password:', error)
      alert('Failed to update password: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateOrganization = async (e) => {
    e.preventDefault()
    if (!selectedOrg) return
    
    setLoading(true)
    
    try {
      const { error } = await updateOrganization(selectedOrg, orgData)
      
      if (error) throw error
      
      alert('Organization updated successfully')
      loadData()
    } catch (error) {
      console.error('Error updating organization:', error)
      alert('Failed to update organization: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const toggleDarkMode = () => {
    const html = document.documentElement
    if (darkMode) {
      html.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    } else {
      html.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    }
    setDarkMode(!darkMode)
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-sm sm:text-base text-muted-light dark:text-muted-dark mt-1 sm:mt-2">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {/* Profile Settings */}
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <User className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
            <h2 className="text-lg sm:text-xl font-semibold">Profile</h2>
          </div>
          
          {/* Avatar Upload Section */}
          <div className="mb-6">
            <label className="block text-xs sm:text-sm font-medium mb-4">Profile Picture</label>
            <div className="flex items-center gap-5">
              {/* Clickable Avatar Circle */}
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => !uploadingAvatar && avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden group focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed"
                  title="Change profile picture"
                >
                  {/* Always render initials as background layer */}
                  <div className="absolute inset-0 bg-gradient-to-br from-accent to-purple-500 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold select-none">
                    {(profileData.full_name?.[0] || profileData.email?.[0] || '?').toUpperCase()}
                  </div>

                  {/* Avatar image on top - hides initials when loaded */}
                  {avatarPreview && !avatarImgError && (
                    <img
                      src={avatarPreview}
                      alt={profileData.full_name || 'Avatar'}
                      className="absolute inset-0 w-full h-full object-cover"
                      onLoad={() => console.log('[Avatar] <img> loaded successfully')}
                      onError={(e) => {
                        console.error('[Avatar] <img> onError fired! src (first 100):', avatarPreview?.substring(0, 100))
                        setAvatarImgError(true)
                      }}
                    />
                  )}

                  {/* Hover overlay */}
                  {uploadingAvatar ? (
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-1">
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span className="text-white text-[10px] font-medium">Uploading</span>
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-200 flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                      <Camera className="w-5 h-5 text-white" />
                      <span className="text-white text-[10px] font-semibold tracking-wide">Change</span>
                    </div>
                  )}
                </button>

                {/* Ring decoration */}
                <div className="absolute inset-0 rounded-full ring-2 ring-gray-200 dark:ring-gray-700 pointer-events-none" />
              </div>

              {/* Info & actions */}
              <div className="flex flex-col gap-1.5 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {profileData.full_name || profileData.email || 'Your name'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  JPG, PNG, GIF or WebP · Max 2 MB
                </p>

                {/* Status messages */}
                {avatarSuccess && (
                  <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                    <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                    {avatarSuccess}
                  </div>
                )}
                {avatarError && (
                  <div className="flex items-center gap-1.5 text-red-500 text-xs font-medium">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    {avatarError}
                  </div>
                )}

                {/* Actions */}
                {!uploadingAvatar && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      className="text-xs font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                      {avatarPreview ? 'Change photo' : 'Upload photo'}
                    </button>
                    {avatarPreview && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          className="text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>
          
          <form onSubmit={handleUpdateProfile} className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">Full Name</label>
              <input
                type="text"
                value={profileData.full_name}
                onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">Email Address</label>
              <input
                type="email"
                value={profileData.email}
                disabled
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 outline-none cursor-not-allowed text-sm sm:text-base"
              />
              <p className="text-xs text-muted-light dark:text-muted-dark mt-1">
                Email address cannot be changed
              </p>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 text-sm sm:text-base w-full sm:w-auto"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </form>
        </div>

        {/* Password Settings */}
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
            <h2 className="text-lg sm:text-xl font-semibold">Change Password</h2>
          </div>
          
          <form onSubmit={handleUpdatePassword} className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">New Password</label>
              <input
                type="password"
                value={passwordData.new}
                onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                placeholder="Enter new password"
                minLength={6}
              />
            </div>
            
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">Confirm Password</label>
              <input
                type="password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                placeholder="Re-enter new password"
                minLength={6}
              />
            </div>
            
            <button
              type="submit"
              disabled={loading || !passwordData.new || !passwordData.confirm}
              className="flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 text-sm sm:text-base w-full sm:w-auto"
            >
              <Save className="w-4 h-4" />
              Update Password
            </button>
          </form>
        </div>

        {/* Organization Settings */}
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
            <h2 className="text-lg sm:text-xl font-semibold">Organization Settings</h2>
          </div>
          
          <div className="mb-3 sm:mb-4">
            <label className="block text-xs sm:text-sm font-medium mb-2">Select Organization</label>
            <select
              value={selectedOrg || ''}
              onChange={(e) => setSelectedOrg(e.target.value)}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
            >
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
          
          <form onSubmit={handleUpdateOrganization} className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">Organization Name</label>
              <input
                type="text"
                value={orgData.name}
                onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                placeholder="Organization name"
              />
            </div>
            
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">Description</label>
              <textarea
                value={orgData.description}
                onChange={(e) => setOrgData({ ...orgData, description: e.target.value })}
                rows={3}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                placeholder="Organization description"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading || !selectedOrg}
              className="flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 text-sm sm:text-base w-full sm:w-auto"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </form>
        </div>

        {/* Appearance Settings */}
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            {darkMode ? <Moon className="w-5 h-5 sm:w-6 sm:h-6 text-accent" /> : <Sun className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />}
            <h2 className="text-lg sm:text-xl font-semibold">Appearance</h2>
          </div>
          
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium mb-1 text-sm sm:text-base">Dark Mode</h3>
              <p className="text-xs sm:text-sm text-muted-light dark:text-muted-dark">
                Toggle between light and dark mode
              </p>
            </div>
            <button
              onClick={toggleDarkMode}
              className={`relative w-12 h-7 sm:w-14 sm:h-8 rounded-full transition-colors shrink-0 ${
                darkMode ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white transition-transform ${
                  darkMode ? 'translate-x-5 sm:translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
            <h2 className="text-lg sm:text-xl font-semibold">Notifications</h2>
          </div>
          
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium mb-1 text-sm sm:text-base">Comment Notifications</h3>
                <p className="text-xs sm:text-sm text-muted-light dark:text-muted-dark">
                  Receive notifications when new comments are added
                </p>
              </div>
              <button
                onClick={() => setNotifications({ ...notifications, email_on_comment: !notifications.email_on_comment })}
                className={`relative w-12 h-7 sm:w-14 sm:h-8 rounded-full transition-colors shrink-0 ${
                  notifications.email_on_comment ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <div
                  className={`absolute top-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white transition-transform ${
                    notifications.email_on_comment ? 'translate-x-5 sm:translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium mb-1 text-sm sm:text-base">Approval Notifications</h3>
                <p className="text-xs sm:text-sm text-muted-light dark:text-muted-dark">
                  Receive notifications when projects are approved
                </p>
              </div>
              <button
                onClick={() => setNotifications({ ...notifications, email_on_approval: !notifications.email_on_approval })}
                className={`relative w-12 h-7 sm:w-14 sm:h-8 rounded-full transition-colors shrink-0 ${
                  notifications.email_on_approval ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <div
                  className={`absolute top-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white transition-transform ${
                    notifications.email_on_approval ? 'translate-x-5 sm:translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium mb-1 text-sm sm:text-base">Project Updates</h3>
                <p className="text-xs sm:text-sm text-muted-light dark:text-muted-dark">
                  Receive notifications when projects are updated
                </p>
              </div>
              <button
                onClick={() => setNotifications({ ...notifications, email_on_project_update: !notifications.email_on_project_update })}
                className={`relative w-12 h-7 sm:w-14 sm:h-8 rounded-full transition-colors shrink-0 ${
                  notifications.email_on_project_update ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <div
                  className={`absolute top-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white transition-transform ${
                    notifications.email_on_project_update ? 'translate-x-5 sm:translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
